import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';

import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import { CreateArticleForm } from '~/components/forms/ArticleForm';
import { useAppPreviewDispatch } from '~/components/organisms/AppPreview';
import { CategoryBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { useNavigate } from '~/components/utils/Navigation';

const MIN_WIDTH_FOR_PREVIEW = 1600;
const ARTICLE_UPDATE_DEBOUNCE_DELAY = 300;

const INITIAL_ARTICLE = {
  _id: '__fake_id',
  title: '',
  categories: [],
};

export type IAppCreateArticlePageProps = {
  appId: string;
  parentCategoryId?: string;
};

export const AppCreateArticlePage: React.FC<IAppCreateArticlePageProps> = ({
  appId,
  parentCategoryId,
}) => {
  const { t } = useTranslation();
  const appPreviewDispatch = useAppPreviewDispatch();
  const [article, setArticle] = useState<IApiGetArticleResponseBody>({
    ...INITIAL_ARTICLE,
    appId,
  });
  const navigate = useNavigate();

  // TODO: create a hook to auto open the preview
  useEffect(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth > MIN_WIDTH_FOR_PREVIEW) {
      appPreviewDispatch.saveState();
      appPreviewDispatch.setOpened(true, { maximize: true });
    }

    return () => appPreviewDispatch.restoreState();
  }, [appPreviewDispatch]);

  useEffect(() => {
    appPreviewDispatch.navigateToRoute({
      appId,
      path: '/articlePreview',
      tab: 'categories',
      navigateOptions: {
        props: { article },
        animate: false,
      },
    });
  }, [appId, appPreviewDispatch, article]);

  const setArticleDebounced = useDebounceCallback(
    setArticle,
    ARTICLE_UPDATE_DEBOUNCE_DELAY,
  );

  return (
    <>
      <Helmet>
        <title>{t('AppCreateArticlePage.seo.title')}</title>
      </Helmet>

      <CategoryBreadcrumbs appId={appId} categoryId={parentCategoryId} />

      <CreateArticleForm
        appId={appId}
        parentCategoryId={parentCategoryId}
        onArticleChange={setArticleDebounced}
        onSuccess={(_, values) =>
          values.categoryIds?.length
            ? navigate(`/apps/${appId}/categories/${values.categoryIds[0]}`)
            : undefined
        }
        // TODO:
        // cancelUrl={
        //   parentCategoryId
        //     ? `/apps/${appId}/categories/${parentCategoryId}`
        //     : `/apps/${appId}/categories`
        // }
      />
    </>
  );
};
